/**
 * AK-738 — статус-борд синхронизации AgroHistory во вкладке «Интеграции»
 * настроек компании (`/company/integrations`). Компактно: строка на
 * сущность (посевы / операции / контуры полей / привязки полей), иконка
 * статуса, «синхронизировано N назад», ключевые счётчики, раскрываемая
 * ошибка. Read-only — ни одна кнопка здесь ничего не запускает.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, CircleDashed, Clock, PauseCircle, XCircle } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { cn } from '@/lib/utils';
import { formatRelativeRu } from '@/lib/dateFmt';
import {
  AGROHISTORY_COUNTER_LABELS,
  getAgroHistorySyncStatus,
  type AgroHistoryEntityStatus,
  type AgroHistoryEntityStatusValue,
} from '@/lib/integrations/agrohistoryStatus';

const STATUS_META: Record<
  AgroHistoryEntityStatusValue,
  { label: string; badgeVariant: BadgeProps['variant']; Icon: typeof CheckCircle2 }
> = {
  ok: { label: 'Синхронизировано', badgeVariant: 'primary', Icon: CheckCircle2 },
  truncated: { label: 'Курсор удержан', badgeVariant: 'accent-brand', Icon: PauseCircle },
  error: { label: 'Ошибка', badgeVariant: 'destructive', Icon: XCircle },
  running: { label: 'Выполняется', badgeVariant: 'secondary', Icon: Clock },
  not_run: { label: 'Не запускалось', badgeVariant: 'outline', Icon: CircleDashed },
};

/** Только скаляры — вложенный объект в counters иначе отрендерится как `[object Object]`. */
function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

function CounterRow({ counters }: { counters: Record<string, unknown> }) {
  const entries = Object.entries(counters).filter(([, v]) => isScalar(v));
  if (entries.length === 0) return null;
  return (
    <p className="text-sm text-muted-foreground">
      {/* «label: value», а не «value label» — подписи в родительном падеже
          множественного числа («посевов добавлено»), склейка спереди ломает
          грамматику при value=1 и превращает нечисловые значения (skipped_reason)
          в бессвязный набор слов. */}
      {entries
        .map(([key, value]) => `${AGROHISTORY_COUNTER_LABELS[key] ?? key}: ${value}`)
        .join(' · ')}
    </p>
  );
}

/**
 * «Синхронизировано …» — ТОЛЬКО из ``last_success_at``. Смешивать его с
 * ``last_run_at`` при упавшем/удержанном прогоне даёт лживую строку вида
 * «синхронизировано 5 часов назад» + бейдж «Ошибка», хотя успешной синхры
 * не было никогда (найдено head-web review PR #887) — борд как раз для того
 * и существует, чтобы честно отвечать «свежие ли данные».
 */
function TimeLabels({ entity }: { entity: AgroHistoryEntityStatus }) {
  if (entity.status === 'not_run') return null;
  const successText = entity.last_success_at
    ? `синхронизировано ${formatRelativeRu(entity.last_success_at)}`
    : 'успешных синхронизаций не было';
  // «Попытка …» — отдельной строкой, только когда последний прогон НЕ ok
  // (иначе это дублирует successText тем же временем).
  const showAttempt = entity.status !== 'ok' && !!entity.last_run_at;
  return (
    <div className="flex flex-col items-end text-sm text-muted-foreground">
      <span>{successText}</span>
      {showAttempt && <span>попытка {formatRelativeRu(entity.last_run_at)}</span>}
    </div>
  );
}

function EntityRow({ entity }: { entity: AgroHistoryEntityStatus }) {
  const [expanded, setExpanded] = useState(false);
  const meta = STATUS_META[entity.status];
  const { Icon } = meta;

  return (
    <div
      data-testid={`agrohistory-status-row-${entity.key}`}
      className="flex min-h-10 flex-col gap-1 border-b border-border py-2 last:border-b-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              entity.status === 'ok' && 'text-primary',
              entity.status === 'error' && 'text-destructive',
              entity.status === 'truncated' && 'text-accent-brand',
              entity.status === 'running' && 'text-muted-foreground',
              entity.status === 'not_run' && 'text-muted-foreground',
            )}
          />
          <span className="font-medium text-foreground">{entity.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <TimeLabels entity={entity} />
          <Badge variant={meta.badgeVariant}>{meta.label}</Badge>
        </div>
      </div>
      <CounterRow counters={entity.counters} />
      {entity.error && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
            {expanded ? 'Скрыть ошибку' : 'Показать ошибку'}
          </button>
          {expanded && (
            // max-h + overflow-auto: журнал прогона может отдать многострочный
            // traceback/SQL-текст — без ограничения он растянет карточку на
            // экраны. break-all — длинный неразрывный токен (URL/DSN/base64)
            // иначе уедет за правый край и даст горизонтальный скролл.
            <p className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md bg-secondary p-2 text-sm text-foreground">
              {entity.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <Stack gap={2}>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </Stack>
  );
}

export function AgroHistoryStatusBoard() {
  const query = useQuery({
    queryKey: ['integrations', 'agrohistory', 'status'],
    queryFn: getAgroHistorySyncStatus,
    staleTime: 60_000,
    // Пока хоть одна сущность «выполняется», статус мог смениться в фоне —
    // опрашиваем чаще, иначе running висит до перемонтирования вкладки.
    refetchInterval: (q) => (q.state.data?.entities.some((e) => e.status === 'running') ? 15_000 : false),
  });

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Синхронизация с AgroHistory</CardTitle>
        <p className="text-sm text-muted-foreground">
          Статус данных, перенесённых из истории хозяйства AgroHistory: когда в последний раз
          обновлялась каждая сущность.
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {query.isLoading && <BoardSkeleton />}
        {query.isError && (
          <p className="text-sm text-destructive">Не удалось загрузить статус синхронизации.</p>
        )}
        {query.data && query.data.entities.map((entity) => <EntityRow key={entity.key} entity={entity} />)}
      </CardContent>
    </Card>
  );
}

export default AgroHistoryStatusBoard;
